import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import type { AccrualLine, PhysicianAccrual, StaffEarnings, PayoutEvent } from '@dru/types';
import { BRAND, EXAM_TYPE_LABEL, PAYOUT_EVENT_LABEL, formatPaise } from '@dru/types';
import { Card, CardHeader, DataTable, Alert, EmptyState, Field, InfoTip, KeyFigures, Select, StatusBadge, TextSkeleton, type Tone } from '@dru/ui';
import { api } from '../../../lib/api';
import { useQuery } from '../../../lib/useQuery';
import { useAuth } from '../../../lib/auth';
import { formatDate } from '../../../lib/format';
import { PageHeader, holds } from '../../../layouts/ConsoleShell';
import { periodLabel } from '../../hr/lib/billing-view';
import { statementState, type OwnStatementRow } from '../../partner/routes/Earnings';

/**
 * What I have earned, and what I have been paid.
 *
 * Reached by a physician or a pathologist — the two roles that produce a
 * billable sign-off. The endpoints take no user id at all: identity comes
 * from the token, so there is nowhere on this screen to ask about anyone else.
 *
 * Two ledgers, one per kind of work, and each person sees the one that pays
 * them. A pathologist's released results are counted in the staff ledger,
 * by month. A physician's signed reviews are paid through settlement
 * statements (never both, or one signature would be paid twice) — so a
 * physician is shown their statements, not an empty monthly ledger that
 * could only ever read zero.
 *
 * The staff figures are derived from release events rather than stored,
 * which has one consequence worth showing rather than hiding: a period can
 * contain work that no published rate card priced. That is a missing rate
 * card, not a month of unpaid work, and saying so is the difference between
 * a question for the finance team and a worrying zero.
 */

/** Whole months, because that is how people are actually paid. */
function monthOptions(count = 12): Array<{ value: string; label: string; from: string; to: string }> {
  const out = [];
  const now = new Date();
  for (let i = 0; i < count; i++) {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
    const from = d.toISOString().slice(0, 10);
    const end = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0));
    out.push({
      value: from,
      label: d.toLocaleDateString('en-IN', { month: 'long', year: 'numeric', timeZone: 'UTC' }),
      from,
      to: end.toISOString().slice(0, 10),
    });
  }
  return out;
}

export function MyEarnings() {
  const months = monthOptions();
  const [month, setMonth] = useState(months[0]!.value);
  const period = months.find((m) => m.value === month)!;

  // Every hook before any early return: a hook after one runs on some renders
  // only, and React loses track of which state is which.
  const { permissions } = useAuth();
  const canRead = holds(permissions, 'staffpay.read-own');
  // The ledger counts lab releases only.
  const releases = holds(permissions, 'lab.result-release');
  const hasStatements = holds(permissions, 'settlement.read-own');
  const showLedger = canRead && (releases || !hasStatements);
  // A physician is paid per signed review through settlement: they see what
  // they have earned before Finance closes anything, then the statements.
  const physician = hasStatements && holds(permissions, 'physician.certify');

  const earnings = useQuery<StaffEarnings | null>(
    () => (showLedger
      ? api.get<StaffEarnings>(`/staff-payout/earnings/mine?from=${period.from}&to=${period.to}`)
      : Promise.resolve(null)),
    [period.from, period.to, showLedger],
  );
  // The last twelve months side by side: the same endpoint asked once per
  // month, so each row is exactly what that month's screen shows. This is the
  // payout history — there is no per-payment list for one's own account.
  const history = useQuery<Array<{ month: (typeof months)[number]; e: StaffEarnings }> | null>(
    () => (showLedger
      ? Promise.all(months.map((m) => api.get<StaffEarnings>(`/staff-payout/earnings/mine?from=${m.from}&to=${m.to}`)
        .then((e) => ({ month: m, e }))))
      : Promise.resolve(null)),
    [showLedger],
  );
  const statements = useQuery<{ items: OwnStatementRow[] } | null>(
    () => (hasStatements ? api.get('/settlements/mine') : Promise.resolve(null)), [hasStatements],
  );
  const accrual = useQuery<PhysicianAccrual | null>(
    () => (physician
      ? api.get<PhysicianAccrual>(`/settlements/mine/accrual?from=${period.from}&to=${period.to}`)
      : Promise.resolve(null)),
    [period.from, period.to, physician],
  );

  if (!canRead && !hasStatements) {
    return (
      <div className="space-y-4">
        <PageHeader className="mb-0" title="My earnings" />
        <EmptyState
          title="This role has no earnings ledger"
          hint="Earnings are counted for pathologists who release results and physicians who sign reviews. Ask your administrator if you think your work should be counted here."
        />
      </div>
    );
  }

  const e = earnings.data;

  return (
    <div className="space-y-4">
      <PageHeader
        className="mb-0"
        title="My earnings"
        hint={showLedger
          ? 'Counted from the lab results you released, priced at the rate in force on the day of each release.'
          : physician
            ? 'What your signed reviews have earned, priced at the rate in force on the day you signed. Earned, settled and paid are three different things, shown apart.'
            : `Your signed fitness reviews are paid through settlement statements, one per period, prepared by ${BRAND.name} finance.`}
        actions={(showLedger || physician) && (
          <Field label="Period" className="w-full sm:w-52">
            {(p) => (
              <Select {...p} value={month} onChange={(ev) => setMonth(ev.target.value)}>
                {months.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </Select>
            )}
          </Field>
        )}
      />

      {showLedger && (
        <>
          {earnings.error && <Alert tone="danger">{earnings.error}</Alert>}

          {/* Three figures, in the order somebody reads them: what the work was
              worth, what has been settled, what is outstanding. */}
          <KeyFigures
            label={`Earnings, ${period.label}`}
            className="lg:grid-cols-3"
            figures={[
              { key: 'earned', label: 'Earned', value: e ? formatPaise(e.earnedPaise) : '—', loading: earnings.loading, hint: period.label },
              { key: 'paid', label: 'Paid', value: e ? formatPaise(e.paidPaise) : '—', loading: earnings.loading, hint: 'Recorded by finance' },
              {
                key: 'out', label: 'Pending payout', value: e ? formatPaise(e.pendingPaise) : '—', loading: earnings.loading,
                /* A negative balance is a real state — an advance, or a reversal
                   landing after the period — so it is shown, not clamped to zero. */
                hint: e && e.pendingPaise < 0 ? 'Paid ahead of earnings' : 'Earned, not yet paid',
                tone: e && e.pendingPaise > 0 ? 'dueSoon' : undefined,
              },
            ]}
          />

          {e && e.unpricedCount > 0 && (
            <Alert tone="warning">
              {e.unpricedCount === 1 ? 'One item is' : `${e.unpricedCount} items are`} not priced,
              because no rate card was published covering the date{e.unpricedCount === 1 ? '' : 's'} it
              happened. {e.unpricedCount === 1 ? 'It is' : 'They are'} counted below but not included in the
              total. Ask your administrator to publish a rate card for that period.
            </Alert>
          )}

          <Card>
            <CardHeader
              title="What you did"
              hint="Each item is priced at the rate that applied on the day it happened."
            />
            <DataTable<StaffEarnings['lines'][number]>
              rows={e?.lines ?? []}
              loading={earnings.loading}
              error={earnings.error}
              onRetry={earnings.reload}
              rowKey={(l) => l.event}
              empty={{
                title: `Nothing in ${period.label}`,
                hint: releases
                  ? 'Results you release appear here once you release them. Choose another month to see earlier work.'
                  : 'Nothing is counted in this ledger for your role.',
              }}
              columns={[
                {
                  key: 'event', header: 'Work', alwaysVisible: true,
                  cell: (l) => PAYOUT_EVENT_LABEL[l.event as PayoutEvent] ?? l.event,
                },
                { key: 'count', header: 'Count', align: 'right', cell: (l) => <span className="tabular-nums">{l.count}</span> },
                {
                  key: 'rate', header: 'Rate', align: 'right',
                  // Null when two cards applied across the period, and also when no
                  // card priced anything at all. An averaged rate is a number that
                  // was never anybody's rate, so neither case shows one, but they
                  // are different facts and "mixed" was a lie for the second.
                  cell: (l) => (l.ratePaise !== null
                    ? <span className="whitespace-nowrap tabular-nums">{formatPaise(l.ratePaise)}</span>
                    : l.unpricedCount >= l.count
                      ? <span className="text-neutral-500" title="No published rate card covers this work">Not priced</span>
                      : <span className="text-neutral-500" title="More than one rate applied in this period">More than one rate</span>),
                },
                {
                  key: 'amount', header: 'Amount', align: 'right',
                  cell: (l) => <span className="whitespace-nowrap tabular-nums font-medium">{formatPaise(l.amountPaise)}</span>,
                },
                {
                  key: 'unpriced', header: 'Not priced', align: 'right', defaultHidden: true,
                  cell: (l) => (l.unpricedCount ? <span className="tabular-nums">{l.unpricedCount}</span> : '—'),
                },
              ]}
            />
          </Card>

          <PayoutHistory history={history} current={month} onPick={setMonth} />

          <p className="flex items-center gap-1 text-caption text-neutral-500">
            Payments are recorded by finance; you cannot change them here.
            <InfoTip label="About a figure that does not match">
              If a figure here does not match what you expected, the rate card in force for the period is
              the place to start.
            </InfoTip>
          </p>
        </>
      )}

      {physician && <Accrued accrual={accrual} label={period.label} />}

      {hasStatements && (
        <Card>
          <CardHeader
            title="Your settlement statements"
            hint={`One statement per period for the reviews you signed, across every company. Read-only: ${BRAND.name} finance prepares, issues and pays them.`}
          />
          <DataTable<OwnStatementRow>
            rows={statements.data?.items ?? []}
            loading={statements.loading}
            error={statements.error}
            onRetry={statements.reload}
            rowKey={(s) => s.id}
            empty={{
              title: 'No statement issued yet',
              hint: physician
                ? `Your signed reviews above are earned and awaiting settlement. A statement appears here once ${BRAND.name} finance issues one for the period — usually monthly.`
                : `A statement appears here once ${BRAND.name} finance closes a period that includes reviews you signed — usually monthly.`,
            }}
            columns={[
              { key: 'period', header: 'Period', alwaysVisible: true, cell: (s) => (
                <div>
                  <div className="whitespace-nowrap text-sm font-medium text-neutral-900">{periodLabel(s.periodStart, s.periodEnd)}</div>
                  <div className="font-mono text-caption text-neutral-500">{s.reference}</div>
                </div>
              ) },
              { key: 'n', header: 'Reviews', align: 'right', cell: (s) => (
                <span className="tabular-nums">
                  {s.sourceCount}
                  {s.unpricedCount > 0 && <span className="block text-caption text-warning-600">{s.unpricedCount} not priced</span>}
                </span>
              ) },
              { key: 'amt', header: 'Amount', align: 'right', cell: (s) => <span className="whitespace-nowrap tabular-nums">{formatPaise(s.subtotalPaise, s.currency)}</span> },
              { key: 'out', header: 'Outstanding', align: 'right', cell: (s) => (
                <span className={`whitespace-nowrap tabular-nums ${s.outstandingPaise > 0 ? 'font-semibold text-neutral-900' : 'text-neutral-500'}`}>{formatPaise(s.outstandingPaise, s.currency)}</span>
              ) },
              { key: 'issued', header: 'Issued', hideOnMobile: true, cell: (s) => <span className="whitespace-nowrap text-small">{s.issuedAt ? formatDate(s.issuedAt) : 'Not yet'}</span> },
              { key: 'status', header: 'Status', mobileLabel: false, cell: (s) => <StatusBadge tone={statementState(s.status).tone}>{statementState(s.status).label}</StatusBadge> },
            ]}
          />
        </Card>
      )}
    </div>
  );
}

/**
 * Earned so far in the month, before and after Finance.
 *
 * Earned is not settled and settled is not paid, so the three are three
 * figures, never one. Work no published rate covers is counted and named —
 * a figure of ₹0 would say the signature was worth nothing.
 */
function Accrued({ accrual, label }: {
  accrual: { data: PhysicianAccrual | null; loading: boolean; error: string | null; reload: () => void };
  label: string;
}) {
  const a = accrual.data;
  const loading = accrual.loading && !a;
  if (accrual.error) return <Alert tone="danger">{accrual.error}</Alert>;
  if (loading) return <Card className="p-4"><TextSkeleton lines={5} /></Card>;
  if (!a) return null;

  const settledSomething = a.inSettlementPaise > 0 || a.paidPaise > 0;
  // Work nothing has priced is not work worth ₹0: say so instead of a zero.
  const nothingPriced = a.pricedCount === 0 && a.unpricedCount > 0;
  return (
    <div className="space-y-3">
      {a.signedCount > 0 && (
        <p className="text-body text-neutral-800">
          <strong className="font-semibold">{a.signedCount} review{a.signedCount === 1 ? '' : 's'}</strong> signed in {label}
          {a.pricedCount > 0 && <> · <strong className="font-semibold tabular-nums">{formatPaise(a.accruedPaise, a.currency)}</strong> earned</>}
          {!settledSomething && a.pricedCount > 0 && <> · awaiting settlement</>}
        </p>
      )}

      <KeyFigures
        label={`Reviews, ${label}`}
        className="grid-cols-3 sm:grid-cols-3 lg:grid-cols-3"
        figures={[
          { key: 'signed', label: 'Signed reviews', value: a.signedCount },
          { key: 'priced', label: 'Priced', value: a.pricedCount },
          { key: 'unpriced', label: 'Not priced', value: a.unpricedCount, tone: a.unpricedCount > 0 ? 'dueSoon' : undefined },
        ]}
      />
      <KeyFigures
        label={`Money, ${label}`}
        className="grid-cols-2 sm:grid-cols-2 lg:grid-cols-4"
        figures={[
          {
            key: 'earned', label: 'Earned (accrued)', value: nothingPriced ? '—' : formatPaise(a.accruedPaise, a.currency),
            hint: nothingPriced ? 'Awaiting pricing'
              : a.unpricedCount > 0 ? `Excludes ${a.unpricedCount} awaiting pricing` : 'At the rate in force when signed',
          },
          { key: 'settlement', label: 'In settlement', value: formatPaise(a.inSettlementPaise, a.currency), hint: 'On statements Finance has issued' },
          { key: 'paid', label: 'Paid', value: formatPaise(a.paidPaise, a.currency), hint: 'Statements paid in full' },
          {
            key: 'outstanding', label: 'Outstanding', value: nothingPriced ? '—' : formatPaise(a.outstandingPaise, a.currency),
            hint: nothingPriced ? 'Awaiting pricing' : 'Earned, not yet paid', tone: a.outstandingPaise > 0 ? 'dueSoon' : undefined,
          },
        ]}
      />

      {a.unpricedCount > 0 && (
        <Alert tone="warning" title={`${a.unpricedCount} review${a.unpricedCount === 1 ? '' : 's'} awaiting pricing`}>
          No published physician rate covers {a.unpricedCount === 1 ? 'this review' : 'these reviews'}. Please contact
          {BRAND.name} Finance. The signed work still counts — it is listed below and will be priced once a rate is published.
        </Alert>
      )}

      <Card>
        <CardHeader title="Signed reviews" hint={`Every review you signed in ${label}, newest first.`} />
        {a.lines.length === 0 ? (
          <div className="px-4 pb-4">
            <EmptyState
              title={`No reviews signed in ${label}`}
              hint="Reviews appear here the moment you sign them. Choose another month to see earlier work."
            />
          </div>
        ) : (
          <ul className="divide-y divide-neutral-200">
            {a.lines.map((l) => <AccrualRow key={l.reviewId} l={l} currency={a.currency} />)}
          </ul>
        )}
      </Card>
    </div>
  );
}

const PRICING_STATE: Record<AccrualLine['pricingStatus'], { label: string; tone: Tone }> = {
  PRICED: { label: 'Awaiting settlement', tone: 'pending' },
  IN_SETTLEMENT: { label: 'In settlement', tone: 'info' },
  PAID: { label: 'Paid', tone: 'covered' },
  UNPRICED: { label: 'Awaiting pricing', tone: 'warning' },
};

function AccrualRow({ l, currency }: { l: AccrualLine; currency: string }) {
  const state = PRICING_STATE[l.pricingStatus];
  return (
    <li className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1.5 px-4 py-3">
      <div className="min-w-0 flex-[1_1_14rem]">
        <div className="text-caption font-normal tabular-nums text-neutral-500">{formatDate(l.signedAt)}</div>
        <Link to={`/physician/review/${l.orderId}`} className="inline-flex items-center gap-0.5 text-body font-medium text-neutral-900 hover:text-primary-700 hover:underline">
          {l.employee.fullName} <ChevronRight aria-hidden className="h-4 w-4 text-neutral-400" />
        </Link>
        <div className="text-caption font-normal text-neutral-500">
          <span className="font-mono">{l.employee.employeeCode}</span>{' · '}
          {EXAM_TYPE_LABEL[l.examinationType] ?? l.examinationType.replace(/_/g, ' ').toLowerCase()}
          {l.company ? ` · ${l.company}` : ''}{l.plant ? ` · ${l.plant}` : ''}
        </div>
      </div>
      <div className="flex flex-col items-start gap-1 sm:items-end">
        {/* One line per completed review: its amount is the agreed rate in force on the day it was signed. */}
        <span className="text-caption font-normal text-neutral-500">Agreed rate</span>
        <span className="text-body font-semibold tabular-nums text-neutral-900">
          {l.amountPaise !== null ? formatPaise(l.amountPaise, currency) : <span className="text-small font-normal text-neutral-500">Not priced yet</span>}
        </span>
        <StatusBadge tone={state.tone} className="py-0">
          {state.label}{l.settlementReference ? ` · ${l.settlementReference}` : ''}
        </StatusBadge>
      </div>
    </li>
  );
}

/** Where a month's money stands, in one phrase. */
function monthState(e: StaffEarnings): { label: string; tone: Tone } {
  const items = e.lines.reduce((n, l) => n + l.count, 0);
  if (!items) return { label: 'No work', tone: 'neutral' };
  if (e.earnedPaise === 0 && e.unpricedCount > 0) return { label: 'Awaiting pricing', tone: 'warning' };
  if (e.pendingPaise <= 0 && e.paidPaise > 0) return { label: 'Paid', tone: 'covered' };
  if (e.paidPaise > 0) return { label: 'Part paid', tone: 'info' };
  return { label: 'Awaiting payout', tone: 'pending' };
}

/**
 * Twelve months at a glance — work done, earned, paid, still pending — so
 * "was I paid for August?" is answered without stepping through the picker.
 * Selecting a month opens it above.
 */
function PayoutHistory({ history, current, onPick }: {
  history: { data: Array<{ month: { value: string; label: string }; e: StaffEarnings }> | null; loading: boolean; error: string | null; reload: () => void };
  current: string;
  onPick: (value: string) => void;
}) {
  // Months with nothing earned, paid or done are left out: twelve rows of
  // zeroes hide the two that matter.
  const rows = (history.data ?? []).filter((r) => r.e.lines.some((l) => l.count > 0) || r.e.paidPaise !== 0 || r.e.earnedPaise !== 0);
  const totals = rows.reduce((t, r) => ({ earned: t.earned + r.e.earnedPaise, paid: t.paid + r.e.paidPaise }), { earned: 0, paid: 0 });
  return (
    <Card>
      <CardHeader
        title="Last 12 months"
        hint="Earned, paid and still pending for each month you had work or a payment. Select a month to see its work above."
        actions={rows.length > 0 && (
          <span className="text-small tabular-nums text-neutral-600">
            {formatPaise(totals.earned)} earned · {formatPaise(totals.paid)} paid
          </span>
        )}
      />
      <DataTable<{ month: { value: string; label: string }; e: StaffEarnings }>
        rows={rows}
        loading={history.loading}
        error={history.error}
        onRetry={history.reload}
        rowKey={(r) => r.month.value}
        onRowClick={(r) => { onPick(r.month.value); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
        empty={{ title: 'No work in the last 12 months', hint: 'Months appear here once you have released results or been paid.' }}
        columns={[
          { key: 'month', header: 'Month', alwaysVisible: true, cell: (r) => (
            <span className={r.month.value === current ? 'font-semibold text-neutral-900' : 'text-neutral-800'}>{r.month.label}</span>
          ) },
          { key: 'n', header: 'Work items', align: 'right', hideOnMobile: true, cell: (r) => (
            <span className="tabular-nums">{r.e.lines.reduce((n, l) => n + l.count, 0)}</span>
          ) },
          { key: 'earned', header: 'Earned', align: 'right', cell: (r) => <span className="whitespace-nowrap tabular-nums">{formatPaise(r.e.earnedPaise)}</span> },
          { key: 'paid', header: 'Paid', align: 'right', cell: (r) => <span className="whitespace-nowrap tabular-nums">{formatPaise(r.e.paidPaise)}</span> },
          { key: 'pending', header: 'Pending', align: 'right', cell: (r) => (
            <span className={`whitespace-nowrap tabular-nums ${r.e.pendingPaise > 0 ? 'font-semibold text-neutral-900' : 'text-neutral-500'}`}>{formatPaise(r.e.pendingPaise)}</span>
          ) },
          { key: 'state', header: 'Status', mobileLabel: false, cell: (r) => {
            const st = monthState(r.e);
            return <StatusBadge tone={st.tone}>{st.label}</StatusBadge>;
          } },
        ]}
      />
    </Card>
  );
}
